import { ErrorBoundary } from 'react-error-boundary';
import { Route, Switch, useLocation } from 'wouter';
import ErrorPage from '../../ErrorPage';
import App, { AuthedPage, HeaderFooterLayout } from '../app';
import Dashboard from '../dashboard';
import DashboardHomePanel from '../dashboard/home/DashboardHomePanel';
import { ROUTES } from './consts';

import { ReactElement } from 'react';
import { RouterProvider } from 'react-aria';
import { ApplicationStartPage } from '../auth/account-verification/ApplicationStartPage';
import { EmailVerificationPage } from '../auth/account-verification/EmailVerificationPage';
import { OneTimeLoginPage } from '../auth/account-verification/OneTimeLoginPage';
import { ChangePasswordPage } from '../auth/forgot-password/ChangePasswordPage';
import { ForgotPasswordPage } from '../auth/forgot-password/ForgotPasswordPage';
import { UpdatePasswordPage } from '../auth/forgot-password/UpdatePasswordPage';
import { HomeSignIn } from '../auth/sign-in/HomeSignIn';
import { HomeSignUp } from '../auth/sign-up/HomeSignUp';
import { CommunityHomePage } from '../dashboard/community/community-home/CommunityHomePage';
import { CommunityWinsPage } from '../dashboard/community/community-wins/CommunityWinsPage';
import { EventsPage } from '../dashboard/community/events/EventsPage';
import { FundingOpportunitiesPage } from '../dashboard/community/funding-opportunities/FundingOpportunitiesPage';
import { JobPostingsPage } from '../dashboard/community/job_openings/JobOpeningsPage';
import MyProjectsPage from '../dashboard/my-projects';
import Healthcheck from '../Healthcheck';
import BusinessChallengesForm from '../auth/sign-up/org-business-challenges';
import UpdateYourEmail from '../dashboard/UpdateYourEmail';
import HomePage from '../home';
import SocialEnterprisesPage from '../landing/SocialEnterprisesPage';
import ProfessionalsPage from '../landing/ProfessionalsPage';
import { InviteOrgColleaguePage } from '../dashboard/account/InviteOrgColleaguePage';
import { AdminNav } from '../admin/AdminNav';
import CommunityAnnouncementsAdmin from '../admin/pages/community-announcements/CommunityAnnouncementsAdmin';
import CommunityWinsAdmin from '../admin/pages/community-wins/CommunityWinsAdmin';
import EventsAdmin from '../admin/pages/events/EventsAdmin';
import FundingOpportunitiesAdmin from '../admin/pages/funding-opportunities/FundingOpportunitiesAdmin';
import { IntroductionsAdmin } from '../admin/pages/introductions/IntroductionsAdmin';
import JobPostingsAdmin from '../admin/pages/JobPostings/JobPostingsAdmin';
import { CommunityMeetingDetail } from '../admin/pages/meetings/CommunityMeetingDetail';
import { EIRMeetingDetail } from '../admin/pages/meetings/EIRMeetingDetail';
import { MeetingsAdmin } from '../admin/pages/meetings/MeetingsAdmin';
import { OrganizationsAdmin } from '../admin/pages/organizations/OrganizationsAdmin';
import { OrganizationVetting } from '../admin/pages/organizations/OrganizationVetting';
import { ProfessionalsAdmin } from '../admin/pages/professionals/ProfessionalsAdmin';
import { ProjectReview } from '../admin/pages/projects/ProjectReview';
import { ProjectsAdmin } from '../admin/pages/projects/ProjectsAdmin';

// TODO: Configure with our custom routing options
// Note that this is RAC; may need similar for bare RA?
declare module 'react-aria-components' {
  interface RouterConfig {
    routerOptions: {
      optionOne: boolean;
      optionTwo: string;
    };
  }
}

const ReactAriaRouter = ({ children }: { children: ReactElement }) => {
  const [, setLocation] = useLocation();

  return (
    <RouterProvider
      // TODO: These navigate & useHref functions don't do anything that isn't default wouter behavior
      // yet. They were used to debug intra-app navigation and may need to accept options for supporting
      // analytics reporting
      navigate={(to) => {
        console.log(`Navigating to: ${to}`);
        if (!to.startsWith('/')) {
          window.location.href = to;
        } else {
          setLocation(to, {});
        }
      }}
      useHref={(href) => {
        return href;
      }}
    >
      {children}
    </RouterProvider>
  );
};

export const routerConfig = (
  <Route path={ROUTES.HOME} nest>
    <ReactAriaRouter>
      {/* react-error-boundary is only supported in client contexts */}
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Switch>
          {/* TODO: I moved <App> down to be a child of <Switch> so that its meta tags would be re-applied on
           * ever route change (unless overridden by children). Is this dangerous? */}
          <App>
            <Route path={ROUTES.HOME}>
              <HomePage />
            </Route>
            <Route path='/platform' nest>
              {/* TODO: Remove when we turn the new homepage on; this is a temporary route to allow
               * the new homepage to be tested in production while the old homepage is still live. */}
              <Route path={'/new-homepage'}>
                <HomePage />
              </Route>
              {/* Unauthed pages */}
              <Route path={ROUTES.SOCIAL_ENTERPRISES}>
                <SocialEnterprisesPage />
              </Route>
              <Route path={ROUTES.PROFESSIONALS}>
                <ProfessionalsPage />
              </Route>
              <Route path={ROUTES.AUTH} nest>
                <Switch>
                  <Route path={ROUTES.SIGN_UP}>
                    <HomeSignUp />
                  </Route>
                  <Route path={ROUTES.EMAIL_VERIFICATION}>
                    <EmailVerificationPage />
                  </Route>
                  <Route path={ROUTES.LOGIN}>
                    <HomeSignIn />
                  </Route>
                  <Route path={ROUTES.WELCOME_LOGIN}>
                    <OneTimeLoginPage />
                  </Route>
                  <Route path={ROUTES.FORGOT_PASSWORD}>
                    <ForgotPasswordPage />
                  </Route>
                  <Route path={ROUTES.UPDATE_PASSWORD}>
                    <UpdatePasswordPage />
                  </Route>
                  <Route path={ROUTES.APPLICATION_START}>
                    <ApplicationStartPage />
                  </Route>
                </Switch>
              </Route>
              {/* Authed pages; visiting when unauthed will redirect to sign-in */}
              <Route path={ROUTES.INVITE_ORG_COLLEAGUE}>
                <InviteOrgColleaguePage />
              </Route>
              <Route path={`${ROUTES.BUSINESS_CHALLENGES}/:page?`}>
                <BusinessChallengesForm />
              </Route>
              <Route path={ROUTES.DASHBOARD} nest>
                <Dashboard>
                  <>
                    <Route path={ROUTES.MY_PROJECTS}>
                      <MyProjectsPage />
                    </Route>
                    <Route path={ROUTES.COMMUNITY} nest>
                      <Route path={ROUTES.JOB_OPENINGS}>
                        <JobPostingsPage />
                      </Route>
                      <Route path={ROUTES.EVENTS}>
                        <EventsPage />
                      </Route>
                      <Route path={ROUTES.FUNDING_OPPORTUNITIES}>
                        <FundingOpportunitiesPage />
                      </Route>
                      <Route path={ROUTES.COMMUNITY_WINS}>
                        <CommunityWinsPage />
                      </Route>
                      <Route path='/'>
                        <CommunityHomePage />
                      </Route>
                    </Route>
                    <Route path='/'>
                      <DashboardHomePanel />
                    </Route>
                  </>
                </Dashboard>
              </Route>
              <Route path={ROUTES.UPDATE_YOUR_EMAIL}>
                <UpdateYourEmail />
              </Route>
              <Route path={ROUTES.PROFILE} nest>
                <Route path={ROUTES.CHANGE_PASSWORD}>
                  <ChangePasswordPage />
                </Route>
              </Route>
              <Route path={ROUTES.ADMIN} nest>
                <AuthedPage>
                  <HeaderFooterLayout>
                    <div>
                      <AdminNav />
                      <div className='p-4'>
                        <Switch>
                          <Route path='/job-postings'>
                            <JobPostingsAdmin />
                          </Route>
                          <Route path='/community-wins'>
                            <CommunityWinsAdmin />
                          </Route>
                          <Route path='/community-announcements'>
                            <CommunityAnnouncementsAdmin />
                          </Route>
                          <Route path='/events'>
                            <EventsAdmin />
                          </Route>
                          <Route path='/funding-opportunities'>
                            <FundingOpportunitiesAdmin />
                          </Route>
                          <Route path='/projects'>
                            <ProjectsAdmin />
                          </Route>
                          <Route path='/projects/review/:id'>
                            <ProjectReview />
                          </Route>
                          <Route path='/introductions'>
                            <IntroductionsAdmin />
                          </Route>
                          <Route path='/organizations'>
                            <OrganizationsAdmin />
                          </Route>
                          <Route path='/professionals'>
                            <ProfessionalsAdmin />
                          </Route>
                          <Route path='/organizations/vetting/:organizationId'>
                            <OrganizationVetting />
                          </Route>
                          <Route path='/meetings/community/:id'>
                            <CommunityMeetingDetail />
                          </Route>
                          <Route path='/meetings/eir/:id'>
                            <EIRMeetingDetail />
                          </Route>
                          <Route path='/meetings'>
                            <MeetingsAdmin />
                          </Route>
                        </Switch>
                      </div>
                    </div>
                  </HeaderFooterLayout>
                </AuthedPage>
              </Route>
              <Route path={ROUTES.HEALTHCHECK} component={Healthcheck} />
              {/* Allows redirecting into the error page in additon to the default pass through below */}
              <Route path={ROUTES.ERROR} component={ErrorPage} />
            </Route>
            {/* Default route to show an error page if no other route matches */}
            {/* TODO: Disabled as it was showing at the bottom of pages w/o a footer. But I didn't think I changed that here? */}
            {/* <Route component={ErrorPage} /> */}
          </App>
        </Switch>
      </ErrorBoundary>
    </ReactAriaRouter>
    {/* Show the homepage by default.
     * Note that attempting to render this via '/' under the '/platform' route does not match */}
    <Route path='/platform/'>
      <HomePage />
    </Route>
  </Route>
);
